import React, { useEffect, useRef, useState } from 'react';

import logo from 'assets/images/sotravel-icon.png';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  ChevronDown,
  LogOut,
  Menu as MenuIcon,
  Settings,
  User,
  X,
} from 'lucide-react';
import { useIsMobile } from '_v2/hooks/responsive';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveMerchant } from 'store/slices/userSlice';
import { signOut } from 'store/slices/authSlice';
import { ADMIN_SECTIONS } from '_v2/components/admin/ui';

const navItems = [
  { label: 'Bookings', path: '/bookings' },
  { label: 'Custom itineraries', path: '/custom-itineraries' },
  { label: 'Itineraries', path: ['/trips'], isAdmin: true },
  { label: 'Documentation', path: ['/cms', '/docs-template'], isAdmin: true },
  { label: 'Payment request', path: '/merchant-payment', merchantOnly: true },
  // The /admin/* tools render as a dropdown panel (see AdminNavMenu), not a
  // plain link.
  { label: 'Admin', path: ['/admin', '/merchant-payment'], isAdmin: true, menu: ADMIN_SECTIONS },
];

const routesWithHiddenNavs = [];

const navLinkClass = (active) =>
  `tw-px-3 tw-py-2 tw-text-sm tw-rounded-lg tw-transition-colors tw-whitespace-nowrap ${
    active
      ? 'tw-text-gray-900 tw-font-semibold'
      : 'tw-text-gray-500 tw-font-medium hover:tw-text-gray-900'
  }`;

const initialsOf = (user) => {
  const source = user?.name || user?.email || '';
  const words = source.replace(/@.*$/, '').trim().split(/[\s._-]+/);
  const letters = words
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return letters || 'S';
};

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside]);
}

function Brand() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate('/')}
      className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0"
    >
      <img src={logo} alt="Sotravel" className="tw-w-8 tw-h-8" />
      <span className="tw-hidden sm:tw-flex tw-items-baseline tw-gap-1.5 tw-text-[15px] tw-leading-none">
        <span className="tw-font-bold tw-text-gray-900">Sotravel</span>
        <span className="tw-text-gray-400">Merchant Portal</span>
      </span>
    </button>
  );
}

// The "Admin" nav item: a dropdown panel listing the admin sections with a
// short description each (opens on hover or click, closes on navigation).
function AdminNavMenu({ item, active }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const { pathname } = useLocation();
  useClickOutside(wrapRef, () => setOpen(false));

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <div
      ref={wrapRef}
      className="tw-relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${navLinkClass(active)} tw-inline-flex tw-items-center tw-gap-1`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {item.label}
        <ChevronDown
          className={`tw-w-3.5 tw-h-3.5 tw-transition-transform ${
            open ? 'tw-rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <div className="tw-absolute tw-left-1/2 -tw-translate-x-1/2 tw-top-full tw-pt-2 tw-z-50">
          <div className="tw-w-[340px] tw-max-h-[calc(100vh-80px)] tw-overflow-y-auto tw-bg-white tw-border tw-border-gray-200 tw-rounded-2xl tw-shadow-xl tw-p-2">
            <p className="tw-px-3 tw-pt-2 tw-pb-1 tw-mb-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-400">
              Internal tools
            </p>
            {item.menu.map(({ label, path, icon: Icon, description }) => {
              const sectionActive = pathname.startsWith(path);
              return (
                <Link
                  key={path}
                  to={path}
                  className={`tw-flex tw-items-start tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-xl tw-transition-colors ${
                    sectionActive ? 'tw-bg-gray-50' : 'hover:tw-bg-gray-50'
                  }`}
                >
                  <span className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-lg tw-bg-gray-100 tw-text-gray-600 tw-flex-shrink-0">
                    <Icon className="tw-w-4 tw-h-4" />
                  </span>
                  <span className="tw-min-w-0">
                    <span
                      className={`tw-block tw-text-sm ${
                        sectionActive
                          ? 'tw-font-semibold tw-text-gray-900'
                          : 'tw-font-medium tw-text-gray-800'
                      }`}
                    >
                      {label}
                    </span>
                    <span className="tw-block tw-text-xs tw-text-gray-500">
                      {description}
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  useClickOutside(wrapRef, () => setOpen(false));

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const itemClass =
    'tw-w-full tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors';

  return (
    <div ref={wrapRef} className="tw-relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="tw-flex tw-items-center tw-gap-2 tw-p-1 tw-rounded-full hover:tw-bg-gray-50 tw-transition-colors"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-900 tw-text-white tw-text-xs tw-font-semibold">
          {initialsOf(user)}
        </span>
        <ChevronDown className="tw-w-3.5 tw-h-3.5 tw-text-gray-400" />
      </button>
      {open && (
        <div className="tw-absolute tw-right-0 tw-top-full tw-mt-2 tw-w-60 tw-bg-white tw-border tw-border-gray-200 tw-rounded-2xl tw-shadow-xl tw-p-2 tw-z-50">
          <div className="tw-px-3 tw-py-2">
            <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-truncate tw-mb-0">
              {user?.name || 'Account'}
            </p>
            {user?.email ? (
              <p className="tw-text-xs tw-text-gray-500 tw-truncate tw-mb-0">
                {user.email}
              </p>
            ) : null}
          </div>
          <div className="tw-border-t tw-border-gray-100 tw-my-1" />
          <button
            type="button"
            className={itemClass}
            onClick={() => go('/settings/account')}
          >
            <User className="tw-w-4 tw-h-4 tw-text-gray-400" />
            View account
          </button>
          <button
            type="button"
            className={itemClass}
            onClick={() => go('/settings/tour-assignment')}
          >
            <Settings className="tw-w-4 tw-h-4 tw-text-gray-400" />
            Settings
          </button>
          <div className="tw-border-t tw-border-gray-100 tw-my-1" />
          <button
            type="button"
            className={`${itemClass} tw-text-red-600 hover:tw-bg-red-50`}
            onClick={() => dispatch(signOut())}
          >
            <LogOut className="tw-w-4 tw-h-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function NavigationDrawer({ visible, onClose, isActive }) {
  const isAdmin = useSelector((s) => s.user.user.is_admin);
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const items = isAdmin
    ? navItems.filter((item) => !item.merchantOnly)
    : navItems.filter((item) => !item.isAdmin);

  return (
    <>
      <div
        className={`tw-fixed tw-inset-0 tw-bg-black tw-transition-opacity tw-duration-300 tw-z-40 ${
          visible
            ? 'tw-bg-opacity-50'
            : 'tw-bg-opacity-0 tw-pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-[290px] tw-bg-white tw-z-50 tw-transform tw-transition-transform tw-duration-300 tw-ease-in-out tw-flex tw-flex-col ${
          visible ? 'tw-translate-x-0' : 'tw-translate-x-full'
        }`}
        style={{
          visibility: visible ? 'visible' : 'hidden',
          transition:
            'transform 0.3s ease-in-out, visibility 0s linear ' +
            (visible ? '0s' : '0.3s'),
        }}
      >
        <div className="tw-p-4 tw-border-b tw-flex tw-justify-between tw-items-center">
          <span className="tw-text-base tw-font-semibold">Menu</span>
          <button
            onClick={onClose}
            className="tw-p-2 hover:tw-bg-gray-100 tw-rounded-full"
            aria-label="Close menu"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>
        <div className="tw-flex-1 tw-overflow-y-auto tw-p-3">
          {items.map((item) =>
            item.menu ? (
              <div key={item.label} className="tw-mt-2">
                <p className="tw-px-3 tw-pt-2 tw-pb-1 tw-mb-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-400">
                  {item.label}
                </p>
                {item.menu.map(({ label, path, icon: Icon }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={onClose}
                    className={`tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2.5 tw-rounded-lg tw-text-sm ${
                      pathname.startsWith(path)
                        ? 'tw-bg-gray-50 tw-font-semibold tw-text-gray-900'
                        : 'tw-text-gray-600 hover:tw-bg-gray-50'
                    }`}
                  >
                    <Icon className="tw-w-4 tw-h-4 tw-text-gray-400" />
                    {label}
                  </Link>
                ))}
              </div>
            ) : (
              <Link
                key={item.label}
                to={Array.isArray(item.path) ? item.path[0] : item.path}
                onClick={onClose}
                className={`tw-block tw-px-3 tw-py-2.5 tw-rounded-lg tw-text-sm ${
                  isActive(item.path)
                    ? 'tw-bg-gray-50 tw-font-semibold tw-text-gray-900'
                    : 'tw-text-gray-600 hover:tw-bg-gray-50'
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </div>
        <div className="tw-border-t tw-p-3">
          <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2">
            <span className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-900 tw-text-white tw-text-xs tw-font-semibold tw-flex-shrink-0">
              {initialsOf(user)}
            </span>
            <span className="tw-min-w-0">
              <span className="tw-block tw-text-sm tw-font-semibold tw-text-gray-900 tw-truncate">
                {user?.name || 'Account'}
              </span>
              {user?.email ? (
                <span className="tw-block tw-text-xs tw-text-gray-500 tw-truncate">
                  {user.email}
                </span>
              ) : null}
            </span>
          </div>
          <Link
            to="/settings/account"
            onClick={onClose}
            className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-text-gray-600 hover:tw-bg-gray-50"
          >
            <User className="tw-w-4 tw-h-4 tw-text-gray-400" />
            View account
          </Link>
          <Link
            to="/settings/tour-assignment"
            onClick={onClose}
            className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-text-gray-600 hover:tw-bg-gray-50"
          >
            <Settings className="tw-w-4 tw-h-4 tw-text-gray-400" />
            Settings
          </Link>
          <button
            type="button"
            onClick={() => dispatch(signOut())}
            className="tw-w-full tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-text-red-600 hover:tw-bg-red-50"
          >
            <LogOut className="tw-w-4 tw-h-4" />
            Sign out
          </button>
        </div>
      </div>
    </>
  );
}

function MobileHeader() {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { pathname } = useLocation();
  const isMobile = useIsMobile();

  const isActive = (path) =>
    Array.isArray(path)
      ? path.some((p) => pathname.includes(p))
      : pathname.includes(path);

  useEffect(() => {
    document.body.style.overflow = drawerVisible ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [drawerVisible]);

  const navsHidden = routesWithHiddenNavs.some((route) =>
    pathname.startsWith(route)
  );
  const isBookingsPage = pathname.includes('/bookings');

  if (!isMobile) return null;

  return (
    <>
      <div className="tw-flex tw-items-center tw-h-14 tw-px-4 tw-border-b tw-bg-white">
        <Brand />
        <div className="tw-grow" />
        {!navsHidden && (
          <div className="tw-flex tw-items-center tw-gap-1">
            {isBookingsPage && <MerchantSelect />}
            <button
              onClick={() => setDrawerVisible(true)}
              className="tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center hover:tw-bg-gray-100 tw-rounded-full tw-transition-colors"
              aria-label="Open menu"
            >
              <MenuIcon className="tw-w-5 tw-h-5 tw-text-gray-700" />
            </button>
          </div>
        )}
      </div>

      <NavigationDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        isActive={isActive}
      />
    </>
  );
}

function DesktopHeader() {
  const { pathname } = useLocation();

  const isActive = (path) =>
    Array.isArray(path)
      ? path.some((p) => pathname.includes(p))
      : pathname.includes(path);

  const navsHidden = routesWithHiddenNavs.some((route) =>
    pathname.startsWith(route)
  );

  const isAdmin = useSelector((s) => s.user.user.is_admin);
  const isBookingsPage = pathname.includes('/bookings');

  const items = isAdmin
    ? navItems.filter((item) => !item.merchantOnly)
    : navItems.filter((item) => !item.isAdmin);

  return (
    <div className="tw-flex tw-items-center tw-h-14 tw-px-4 sm:tw-px-6 tw-border-b tw-bg-white">
      <div className="tw-flex-1 tw-flex tw-items-center tw-min-w-0">
        <Brand />
      </div>
      <nav className="tw-flex tw-justify-center tw-items-center tw-gap-1 tw-flex-shrink-0">
        {!navsHidden &&
          items.map((item) =>
            item.menu ? (
              <AdminNavMenu
                key={item.label}
                item={item}
                active={isActive(item.path)}
              />
            ) : (
              <Link
                key={item.label}
                to={Array.isArray(item.path) ? item.path[0] : item.path}
                className={navLinkClass(isActive(item.path))}
              >
                {item.label}
              </Link>
            )
          )}
      </nav>
      <div className="tw-flex-1 tw-flex tw-justify-end tw-items-center tw-gap-3 tw-min-w-0">
        {!navsHidden && isBookingsPage && <MerchantSelect />}
        <UserMenu />
      </div>
    </div>
  );
}

function Header() {
  const isMobile = useIsMobile();

  if (isMobile) return <MobileHeader />;

  return <DesktopHeader />;
}

export default Header;

function MerchantSelect() {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const selectRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadingUserMerchants = useSelector((s) => s.user.loadingUserMerchants);

  const merchantOptions = useSelector(
    (state) => state.user.userMerchants || []
  );

  const activeMerchant = useSelector((s) => s.user.activeMerchant);

  const handleSelect = (merchant) => {
    // If clicking on already selected merchant, clear the selection
    if (activeMerchant?.id === merchant.id) {
      dispatch(setActiveMerchant(null));
    } else {
      dispatch(setActiveMerchant(merchant));
    }
    setIsOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="tw-relative" ref={selectRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 focus:tw-outline-none"
      >
        <span
          className={`tw-truncate tw-max-w-[150px] tw-md:tw-max-w-[200px] ${!activeMerchant ? 'tw-text-gray-400' : ''}`}
        >
          {activeMerchant?.outlet_or_tour_name || 'Select merchant'}
        </span>
        <ChevronDown
          className={`tw-w-4 tw-h-4 tw-text-gray-400 tw-transition-transform ${isOpen ? 'tw-rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="tw-absolute tw-right-0 tw-z-10 tw-mt-1 tw-w-full tw-min-w-[200px] tw-max-h-60 tw-overflow-auto tw-bg-white tw-border tw-rounded-lg tw-shadow-lg">
          <input
            type="text"
            placeholder="Search..."
            className="tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-border-b tw-border-gray-200 focus:tw-outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {merchantOptions.length > 0 ? (
            merchantOptions
              .filter((merchant) =>
                merchant.outlet_or_tour_name
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())
              )
              .map((merchant) => (
                <button
                  key={merchant.id}
                  onClick={() => handleSelect(merchant)}
                  className={`tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-sm hover:tw-bg-gray-100 ${
                    activeMerchant?.id === merchant.id
                      ? 'tw-bg-gray-50 tw-text-blue-600'
                      : ''
                  }`}
                >
                  <span className="tw-block tw-truncate">
                    {merchant.outlet_or_tour_name}
                  </span>
                </button>
              ))
          ) : loadingUserMerchants ? (
            <div className="tw-p-4 tw-text-center tw-text-gray-500">
              Loading merchants...
            </div>
          ) : (
            <div className="tw-p-4 tw-text-center tw-text-gray-500">
              No merchants found.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
